import { Component } from '@angular/core';
import { EntityError } from 'breeze-client';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
  selector: 'prox-save-error-dialog',
  template: `
    <div>
      <div class="text-l mb-3 font-bold">{{ message }}</div>
      <ng-container *ngIf="!!entityErrors">
        <div class="flex" *ngFor="let e of entityErrors">
          <div class="w-1/4">{{ e.errorName }}</div>
          <div class="w-3/4">{{ e.errorMessage }}</div>
        </div>
      </ng-container>
      <div class="mt-3 text-right">
        <button
          (click)="reload()"
          pButton
          type="button"
          icon="pi pi-refresh"
          label="Reload"
          class="p-button-raised p-button-primary mr-3"
        ></button>
        <button (click)="close()" pButton type="button" icon="pi pi-times" label="Close" class="p-button-raised p-button-danger"></button>
      </div>
    </div>
  `,
  standalone: false,
})
export class SaveErrorDialogComponent {
  message?: string;
  entityErrors?: EntityError[];
  data: any;
  constructor(
    dialogConfig: DynamicDialogConfig,
    private ref: DynamicDialogRef,
  ) {
    this.data = dialogConfig.data;
    this.formatError(this.data.error);
  }

  private formatError(err: Error) {
    this.message = err.message;

    const occurred = this.message.indexOf('--An error occurred while saving', 20);
    if (occurred > 0) {
      this.message = this.message.substring(0, occurred);
    }

    this.message = humanizeKnownServerErrors(this.message);

    const ees: EntityError[] = (err as any).entityErrors;
    if (ees && ees.length) {
      for (const e of ees) {
        const entityName = e.entity ? e.entity.toString() : e.errorName;
        e.errorName = e.propertyName ? entityName + ' [' + e.propertyName + ']' : entityName;
      }
      this.entityErrors = ees;
    }
  }

  close() {
    this.ref.close();
  }

  reload() {
    location.reload();
  }
}

/**
 * Rewrite known raw server-error strings into messages a non-developer can act on.
 * Add new patterns here when a recurring raw error appears in the wild. Each pattern
 * should be specific enough that it can't false-positive on unrelated messages.
 */
export function humanizeKnownServerErrors(message: string): string {
  // ASP.NET Identity emits "Username 'X' is already taken." verbatim from
  // UserManager.CreateAsync when the username uniqueness constraint trips.
  // This is the most common surface for the issue today; the proper fix is
  // real-time blur validation on the form, planned for a follow-up branch.
  const usernameTaken = message.match(/Username '([^']+)' is already taken\.?/i);
  if (usernameTaken) {
    return `The Login Name "${usernameTaken[1]}" is already in use by another user. Please choose a different Login Name.`;
  }

  // SQL Server unique-index violation on AspNetUsers.EmailIndex. Surfaces when an
  // email survives the client-side check but collides with an orphan/duplicate
  // AspNetUsers row (see project_email-dup-cleanup-and-migration-bug). Backstop
  // for when EmailAvailabilityService is bypassed.
  const emailTaken = message.match(
    /Cannot insert duplicate key row in object '[^']*AspNetUsers'[^.]*EmailIndex[^.]*\.\s*The duplicate key value is \(([^)]+)\)/i,
  );
  if (emailTaken) {
    return `The email address "${emailTaken[1]}" is already in use by another user. Please choose a different email address.`;
  }

  // SQL Server foreign-key violation on DELETE. Any table whose row is still
  // referenced by another table will surface this. Generic wording so it works
  // for every table without per-FK mapping.
  if (/DELETE statement conflicted with the REFERENCE constraint/i.test(message)) {
    return 'This item cannot be deleted because it is still referenced by other records. Remove those references first, or set the item to Inactive instead.';
  }

  return message;
}
